import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FC, FormEvent, useState } from "react";
import { z } from "zod";
import { SetPasswordStepProps } from "./set-password-step.types";
import { useSetAdminPassword } from "../../queries";
import { useLoginPageStore } from "../../context";

const initialFormState = {
  password: "",
  repeatedPassword: "",
};

const formSchema = z
  .object({
    password: z.string().min(1, "Field is required"),
    repeatedPassword: z.string().min(1, "Field is required"),
  })
  .refine((data) => data.password === data.repeatedPassword, {
    message: "Passwords do not match",
    path: ["repeatedPassword"],
  });

type FormData = z.infer<typeof formSchema>;

const SetPasswordStep: FC<SetPasswordStepProps> = ({ title, description }) => {
  const { adminData } = useLoginPageStore();
  const { setAdminPassword, isSetAdminPasswordLoading } = useSetAdminPassword();

  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatedPassword, setShowRepeatedPassword] = useState(false);
  const [touched, setTouched] = useState({
    password: false,
    repeatedPassword: false,
  });
  const [adminUserFormData, setAdminUserFormData] = useState<Partial<FormData>>(
    {}
  );

  const formData: FormData = {
    ...initialFormState,
    ...adminUserFormData,
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (adminData) {
      setAdminPassword({
        accessToken: adminData.tokens.accessToken,
        currentPassword: adminData.oldPassword,
        newPassword: formData.password,
      });
    }
  };

  const validate = () => {
    const res = formSchema.safeParse(formData);
    if (res.success) {
      return undefined;
    }
    return res.error.format();
  };

  const errors = validate();
  const isFormFilled = Object.values(formData).every((v) => !!v);

  return (
    <Box component="form" width="100%" onSubmit={onSubmit}>
      <Stack justifyContent={"flex-start"} width={"100%"}>
        <Typography variant="h4" mt={4}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ mb: 4 }}>
          {description}
        </Typography>
      </Stack>

      <Stack spacing={2} mb={3}>
        <TextField
          fullWidth
          variant="filled"
          autoComplete="new-password"
          type={showPassword ? "text" : "password"}
          placeholder="Password"
          label={
            touched.password && errors?.password
              ? errors.password._errors[0]
              : "Password"
          }
          error={touched.password && !!errors?.password}
          value={formData.password}
          onBlur={() => setTouched((prev) => ({ ...prev, password: true }))}
          onChange={(e) =>
            setAdminUserFormData((prev) => ({
              ...prev,
              password: e.target.value,
            }))
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          fullWidth
          variant="filled"
          autoComplete="new-password"
          type={showRepeatedPassword ? "text" : "password"}
          placeholder="Repeat Password"
          label={
            touched.repeatedPassword && errors?.repeatedPassword
              ? errors.repeatedPassword._errors[0]
              : "Repeat Password"
          }
          error={touched.repeatedPassword && !!errors?.repeatedPassword}
          value={formData.repeatedPassword}
          onBlur={() =>
            setTouched((prev) => ({ ...prev, repeatedPassword: true }))
          }
          onChange={(e) =>
            setAdminUserFormData((prev) => ({
              ...prev,
              repeatedPassword: e.target.value,
            }))
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowRepeatedPassword((prev) => !prev)}
                  edge="end"
                >
                  {showRepeatedPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Stack>
      <Button
        fullWidth
        color="secondary"
        variant="contained"
        type="submit"
        loading={isSetAdminPasswordLoading}
        disabled={!isFormFilled || !!errors || isSetAdminPasswordLoading}
      >
        Set Password
      </Button>
    </Box>
  );
};

export { SetPasswordStep };
